import { Spacing } from '@persona-id/design-tokens';
import { SlidersIcon } from '@persona-id/design-tokens/icons';
import Fuse from 'fuse.js';
import { union } from 'lodash/fp';
import { ComponentProps, forwardRef, useMemo, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
  ResponderProvided,
} from 'react-beautiful-dnd';
import styled from 'styled-components';

import SearchInput from 'dashboard/components-lib/SearchInput';
import { StyledIconDrag } from 'dashboard/lib/dnd/StyledIconDrag';
import { reorder } from 'dashboard/lib/dnd/utils';
import { AttributeColumn } from 'dashboard/models/table-view';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Popover from 'design-system/components/Popover';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import Tooltip from 'design-system/components/Tooltip';

const DndSection = styled('div')`
  max-height: 400px;

  overflow: scroll;
  ::-webkit-scrollbar {
    display: none;
  }
`;

const DndItemWrapper = styled('div')`
  background: white;
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.color.border.secondary};
  padding: ${Spacing[2]} ${Spacing[2]};

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: left;
`;

const SuffixContainer = styled('div')`
  padding-left: ${Spacing[2]};
  margin-left: auto;
  align-self: center;
`;

const SearchContainer = styled.div`
  display: flex;
`;

const StyledSearchInput = styled(SearchInput)`
  width: 100%;
`;

const PopoverCard = styled(Card)`
  width: 300px;
  padding: ${Spacing[4]};
`;

const TriggerIconButton = forwardRef<HTMLButtonElement, ComponentProps<typeof Button>>(
  function TriggerIconButton({ disabled, ...elemProps }, ref) {
    return (
      <Button
        disabled={disabled}
        width='icon'
        variant='basic'
        {...elemProps}
        ref={ref}
        aria-label='Table Properties'
      >
        <SlidersIcon />
      </Button>
    );
  },
);

const TooltipButtonWrapper = styled('div')`
  display: inline-block;
`;

export interface TableViewConfigurationDropdownProps extends TableViewConfigurationProps {
  disabled: boolean;
  disabledTooltipMessage?: string;
}

const TableViewConfigurationDropdown = ({
  disabled = false,
  disabledTooltipMessage = 'Your role does not have permission to configure the view.',
  ...props
}: TableViewConfigurationDropdownProps) => {
  return disabled ? (
    <Tooltip
      trigger={
        // NB: this wrapping div is needed to properly display the tooltip. The library we're using, floating-ui
        // doesn't support displaying tooltips on disabled buttons.
        <TooltipButtonWrapper>
          <TriggerIconButton disabled={true} />
        </TooltipButtonWrapper>
      }
    >
      {disabledTooltipMessage}
    </Tooltip>
  ) : (
    <Popover>
      <Popover.Trigger asChild>
        <TriggerIconButton disabled={false} />
      </Popover.Trigger>
      <Popover.Content
        style={{ zIndex: Z_INDICES.dropdown }}
        sideOffset={8}
        side='bottom'
        align='end'
      >
        <PopoverCard elevated>
          <TableViewConfiguration {...props} />
        </PopoverCard>
      </Popover.Content>
    </Popover>
  );
};

export interface TableViewConfigurationProps {
  columnConfiguration: AttributeColumn[];
  attributeOptions: AttributeColumn[];
  updateColumnConfiguration: (newConfiguration: AttributeColumn[]) => void;
}

export const TableViewConfiguration = ({
  columnConfiguration,
  attributeOptions,
  updateColumnConfiguration,
}: TableViewConfigurationProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const fuse = useMemo(() => {
    const fuseOptions = {
      shouldSort: false,
      threshold: 0.4,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: [{ name: 'label', weight: 1.0 }],
    };
    return new Fuse(attributeOptions, fuseOptions);
  }, [attributeOptions]);

  // NB: we do this to make sure that attributes that exist in the column configuration but NOT the standardized options still show up in the menu.
  // Options are deduped.
  const allOptions = useMemo(
    () => union(attributeOptions, columnConfiguration),
    [attributeOptions, columnConfiguration],
  );

  const filteredSearchOptions =
    searchTerm === '' ? allOptions : fuse.search(searchTerm).map(({ item }) => item);

  const hiddenAttributes = useMemo(() => {
    return filteredSearchOptions.filter((attribute) => {
      const columnConfigurationKeys = columnConfiguration.map((c) => c.key);
      return !columnConfigurationKeys.includes(attribute.key);
    });
  }, [columnConfiguration, filteredSearchOptions]);

  const filteredShownAttributes = useMemo(() => {
    return columnConfiguration.filter((column) =>
      filteredSearchOptions.map((o) => o.key).includes(column.key),
    );
  }, [columnConfiguration, filteredSearchOptions]);

  const handleDragEnd = (result: DropResult, _provided: ResponderProvided) => {
    const { destination, source } = result;
    if (
      !destination ||
      (destination.droppableId === source.droppableId && destination.index === source.index)
    ) {
      return;
    }

    const reorderedColumns = reorder(columnConfiguration, source.index, destination.index);
    updateColumnConfiguration(reorderedColumns);
  };

  const generateShownAttributesToggle = (column: AttributeColumn) => () => {
    const updatedAttributes = columnConfiguration.filter((a) => a.key !== column.key);
    updateColumnConfiguration(updatedAttributes);
  };

  const generateHiddenAttributesToggle = (column: AttributeColumn) => () => {
    const updatedAttributes = columnConfiguration.concat(column);
    updateColumnConfiguration(updatedAttributes);
  };

  const shownDndItemProps: AttributeDndProps[] = filteredShownAttributes.map((column, index) => {
    return {
      column: column,
      index: index,
      // If there's only one column shown, disable the toggle so we prevent having an empty column configuration.
      isDisabled: columnConfiguration.length === 1,
      isToggled: true,
      shouldDisableDrag: false,
      handleToggle: generateShownAttributesToggle(column),
    } as AttributeDndProps;
  });

  const hiddenDndItemProps: AttributeDndProps[] = hiddenAttributes.map((column, index) => {
    return {
      column: column,
      index: index,
      isToggled: false,
      shouldDisableDrag: true,
      handleToggle: generateHiddenAttributesToggle(column),
    } as AttributeDndProps;
  });

  return (
    <div>
      <Label size='lg' weight='bold'>
        Configure columns
      </Label>
      <Spacer height={2} />
      <SearchContainer>
        <StyledSearchInput
          title={null}
          value={searchTerm}
          handleInput={setSearchTerm}
          size='md'
          placeholder='Search by item...'
          dataTest='checklist-item-panel-item-table__search'
        />
      </SearchContainer>
      <Spacer height={2} />
      {shownDndItemProps.length > 0 || hiddenDndItemProps.length > 0 ? (
        <DndSection>
          {shownDndItemProps.length > 0 && (
            <div>
              <Paragraph>Shown attributes</Paragraph>
              {/* Issue with react-beautiful-dnd and transformed parent elements: https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/reparenting.md
            We have to use the renderClone API to get the dragging working correctly. This has to do with one of the parent elements being a Popover. */}
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable
                  droppableId='cases-view__configure-shown-columns'
                  renderClone={(provided, _snapshot, rubric) => {
                    return (
                      <div
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        ref={provided.innerRef}
                      >
                        <AttributeDnd {...shownDndItemProps[rubric.source.index]} />
                      </div>
                    );
                  }}
                >
                  {(provided) => {
                    return (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        {shownDndItemProps.map((props, idx) => {
                          return (
                            <Draggable
                              key={`${props.column.key}`}
                              draggableId={props.column.key}
                              index={idx}
                            >
                              {(provided) => {
                                return (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    <AttributeDnd {...props} />
                                  </div>
                                );
                              }}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    );
                  }}
                </Droppable>
              </DragDropContext>
            </div>
          )}
          <Spacer height={2} />
          {hiddenDndItemProps.length > 0 && (
            <div>
              <Paragraph>Hidden attributes</Paragraph>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable
                  droppableId='cases-view__configure-hidden-columns'
                  renderClone={(provided, _snapshot, rubric) => {
                    return (
                      <div
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        ref={provided.innerRef}
                      >
                        <AttributeDnd {...hiddenDndItemProps[rubric.source.index]} />
                      </div>
                    );
                  }}
                >
                  {(provided) => {
                    return (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        {hiddenDndItemProps.map((props, idx) => {
                          return (
                            <Draggable
                              key={`${props.column.key}`}
                              draggableId={props.column.key}
                              index={idx}
                              isDragDisabled={props.shouldDisableDrag}
                            >
                              {(provided) => {
                                return (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    <AttributeDnd {...props} />
                                  </div>
                                );
                              }}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    );
                  }}
                </Droppable>
              </DragDropContext>
            </div>
          )}
        </DndSection>
      ) : (
        <div>
          <Paragraph>No matching attributes</Paragraph>
        </div>
      )}
    </div>
  );
};

const OverflowText = styled('div')`
  max-width: 160px;
  flex-wrap: wrap;
  display: flex;
`;

interface AttributeDndProps {
  column: AttributeColumn;
  index: number;
  isDisabled?: boolean;
  isToggled: boolean;
  shouldDisableDrag: boolean;
  handleToggle: () => void;
}

const AttributeDnd = ({
  column,
  index,
  isDisabled = false,
  isToggled,
  shouldDisableDrag,
  handleToggle,
}: AttributeDndProps) => {
  return (
    <DndItemWrapper key={`${column.key}-${index}`}>
      <StyledIconDrag $active $shouldHide={shouldDisableDrag} />
      <OverflowText>
        <Paragraph color='primary' size='sm'>
          {column.label}
        </Paragraph>
      </OverflowText>
      <SuffixContainer>
        <ToggleSlider value={isToggled} disabled={isDisabled} onChange={handleToggle} />
      </SuffixContainer>
    </DndItemWrapper>
  );
};

export default TableViewConfigurationDropdown;
